<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col">
    <!-- Loading State -->
    <Loading v-if="loading" :text="$t('common.loading')" />

    <!-- Goat list -->
    <div
      v-else-if="goats.length > 0"
      class="flex-1 flex flex-col gap-3 container-padding pb-24 overflow-y-auto">
      <ModuleShell
        v-for="goat in goats"
        :key="goat.id"
        :title="goat.eartag || goat.name || (goat.dateOfBirth ? `${goat.dateOfBirth} (DOB)` : 'ID: ' + goat.id)"
        :margin="false"
        :titleClass="'mb-1'"
        @click="openForGoat(goat)">
        <template #action>
          <div class="flex flex-col items-center gap-0.5">
            <CheckCircle
              v-if="completedGoats.includes(goat.id)"
              class="w-5 h-5 text-[var(--color-success)]" />
            <Circle
              v-else
              class="w-5 h-5 text-[var(--color-text)] opacity-25" />

            <!-- Newly entered value (current session) -->
            <div
              v-if="enteredValues[goat.id]"
              class="text-xs font-semibold text-[var(--color-success)] leading-none">
              {{ enteredValues[goat.id] }}
            </div>
            <div
              v-else
              class="text-xs text-[var(--color-text)] opacity-30 leading-none">
              —
            </div>
          </div>
        </template>

        <div class="flex justify-between items-center">
          <p v-if="goat.eartag && goat.name" class="text-sm text-[var(--color-text)] leading-snug">
            {{ goat.name }}
          </p>

          <!-- Previous value (most recent historical) -->
          <p
            v-if="previousValues[goat.id]"
            class="text-xs text-[var(--color-text)] opacity-60 leading-none">
            {{ $t('views.session.previous') }}: {{ previousValues[goat.id] }}
          </p>
        </div>
      </ModuleShell>
    </div>

    <!-- Empty State: From GoatListView.vue -->
    <div
      v-else-if="!loading"
      class="flex-1 flex items-center justify-center px-4">
      <div class="text-center">
        <div class="text-6xl mb-4"></div>
        <h2 class="text-xl font-bold mb-2">
          {{ $t('views.goats.noGoatsYet') }}
        </h2>
        <p class="text-[var(--color-text)] mb-4">
          {{ $t('views.goats.startCreating') }}
        </p>
        <router-link
          to="/new-goat"
          class="inline-block px-6 py-3 bg-[var(--color-primary-200)] text-[var(--color-primary-900)] rounded-lg border border-[var(--color-primary-400)] hover:bg-[var(--color-primary-300)] transition-colors duration-200 font-semibold">
          {{ $t('views.goats.createFirstGoat') }}
        </router-link>
      </div>
    </div>

    <ValueInput
      :show="showDialog"
      :title="selectedGoat.eartag || selectedGoat.name || (selectedGoat.dateOfBirth ? `${selectedGoat.dateOfBirth} (DOB)` : 'ID: ' + selectedGoat.id)"
      :subtitle="selectedGoat.eartag && selectedGoat.name ? selectedGoat.name : ''"
      :goatId="selectedGoat.id || 0"
      :inputLabel="dialogTitle"
      :placeholder="placeholder"
      :inputMode="getInputMode()"
      :validation="getValidation()"
      :showDateInput="true"
      @save="handleValueSaved"
      @cancel="cancelDialog" />

    <Toast
      :show="showToast"
      :message="toastMessage"
      :subtitle="toastSubtitle"
      :show-subtitle="!!toastSubtitle"
      :type="toastType"
      :duration="toastDuration" />

    <PhotoWeightView
      ref="photoWeight"
      :goat="selectedGoat"
      @result="savePhotoWeight"
      @error="handlePhotoWeightError" />
  </div>
</template>

<script setup>
  import PhotoWeightView from '@/components/camera/ml/PhotoWeightView.vue'
  import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
  import Toast from '@/components/ui/feedback/Toast.vue'
  import ValueInput from '@/components/ui/inputs/ValueInput.vue'
  import Loading from '@/components/ui/feedback/Loading.vue'
  import { useRoute } from 'vue-router'
  import { ref, computed } from 'vue'
  import { onMounted } from 'vue'
  import { CheckCircle, Circle } from 'lucide-vue-next'
  import { useI18n } from 'vue-i18n'
  import { useGoatStore } from '@/stores/goat.store.js'
  import { useWeightStore } from '@/stores/weight.store.js'
  import { useFamachaStore } from '@/stores/famacha.store.js'
  import { useDagStore } from '@/stores/dag.store'
  import { useTemperatureStore } from '@/stores/temperature.store.js'
  import { useHealthStore } from '@/stores/health.store.js'
  import { weightRepository } from '@/services/repositories/weight.repository.js'
  import { temperatureRepository } from '@/services/repositories/temperature.repository.js'
  import { famachaRepository } from '@/services/repositories/famacha.repository.js'
  import { healthRepository } from '@/services/repositories/health.repository.js'
  import { storeToRefs } from 'pinia'
  import { useUiStore } from '@/stores/ui.store.js'
  const uiStore = useUiStore()

  const { t } = useI18n()

  const route = useRoute()
  const photoWeight = ref(null)
  const goatStore = useGoatStore()
  const weightStore = useWeightStore()
  const temperatureStore = useTemperatureStore()
  const famachaStore = useFamachaStore()
  const dagStore = useDagStore()
  const { goats: allGoats } = storeToRefs(goatStore)

  // Parse groupId: 'all' for all goats, 'ungrouped' for goats without groups, or numeric group ID
  const groupId =
    route.params.groupId === 'all' || route.params.groupId === 'ungrouped'
      ? route.params.groupId
      : Number(route.params.groupId)

  // Local filtering (no extra DB query, uses cached data)
  const goats = computed(() => {
    if (groupId === 'all') {
      return allGoats.value // Show all goats
    }
    if (groupId === 'ungrouped') {
      return allGoats.value.filter((goat) => !goat.groupId) // Show only ungrouped goats
    }
    return allGoats.value.filter((goat) => goat.groupId === groupId) // Show goats in specific group
  })

  // Session type detection
  const sessionType = computed(() => route.params.type)

  // Dialog + data states
  const showDialog = ref(false)
  const placeholder = ref('')
  const selectedGoat = ref({})
  const enteredValues = ref({}) // Display values in goat list
  const completedGoats = ref([]) // Track which goats have data entered
  const previousValues = ref({}) // Most recent historical values for each goat

  // Toast notification state
  const showToast = ref(false)
  const toastMessage = ref('')
  const toastType = ref('saved')
  const toastDuration = ref(3000)
  const toastSubtitle = ref('')

  // Loading state
  const loading = ref(true)

  // Get localized dialog title based on session type
  const dialogTitle = computed(() => {
    const type = sessionType.value
    if (type.includes('weight')) return t('views.session.weight.label')
    if (type.includes('temp')) return t('views.session.temperature.label')
    if (type.includes('famacha')) return t('views.session.famacha.label')
    if (type.includes('dag')) return t('views.session.dag.label')
    if (type.includes('wound')) return t('views.health.woundsDetail.type')
    if (type.includes('treatment')) return t('views.health.treatmentsDetail.type')
    // wound and treatment have their own dialog titles in dedicated components
    return t('views.session.value')
  })

  onMounted(() => {
    uiStore.setHeader({
      title: t('views.session.selectGoat'),
      showBack: true,
      showAdd: false,
    })
  })

  onMounted(async () => {
    loading.value = true

    try {
      // Ensure all goats are loaded (use cache if available)
      if (allGoats.value.length === 0) {
        await goatStore.fetchGoats()
      }
    } catch (error) {
      console.error('Failed to load goats:', error)
      showToastNotification(t('views.session.loadingError'), 'error')
    } finally {
      loading.value = false
    }

    // Load most recent values for all goats in this group/session
    await loadPreviousValues()

    // Don't load existing values - session is for NEW entries only
    // enteredValues and completedGoats will be populated as user enters data
  })

  // Load the most recent historical value for each goat based on session type
  async function loadPreviousValues() {
    const type = sessionType.value

    for (const goat of goats.value) {
      try {
        let latestRecord = null

        if (type.includes('weight')) {
          latestRecord = await weightRepository.latest(goat.id)
          if (latestRecord) {
            previousValues.value[goat.id] = `${latestRecord.value} kg`
          }
        } else if (type.includes('temp')) {
          latestRecord = await temperatureRepository.latest(goat.id)
          if (latestRecord) {
            previousValues.value[goat.id] = `${latestRecord.value} °C`
          }
        } else if (type.includes('famacha')) {
          latestRecord = await famachaRepository.latest(goat.id)
          if (latestRecord) {
            previousValues.value[goat.id] = `${latestRecord.score}/5`
          }
        } else if (type.includes('wound')) {
          const woundRecords = await healthRepository.findWoundsByGoat(goat.id)
          if (woundRecords && woundRecords.length > 0) {
            const latest = woundRecords[woundRecords.length - 1]
            previousValues.value[goat.id] = latest.title.substring(0, 20) + (latest.title.length > 20 ? '...' : '')
          }
        } else if (type.includes('treatment')) {
          const treatmentRecords = await healthRepository.findTreatmentsByGoat(goat.id)
          if (treatmentRecords && treatmentRecords.length > 0) {
            const latest = treatmentRecords[treatmentRecords.length - 1]
            previousValues.value[goat.id] = latest.title.substring(0, 20) + (latest.title.length > 20 ? '...' : '')
          }
        }
        // DAG can be added later when store is available
      } catch (err) {
        console.error(`Failed to load previous value for goat ${goat.id}:`, err)
      }
    }
  }

  // Open input dialog or camera for selected goat
  function openForGoat(goat) {
    selectedGoat.value = goat

    // Photo mode: open camera instead of dialog
    if (sessionType.value === 'weight-photo') {
      photoWeight.value.open()
      return
    }

    
    // Placeholder and input mode based on session type
    const type = sessionType.value

    if (type.includes('weight')) {
      placeholder.value = t('views.session.weight.placeholder')
    } else if (type.includes('temp')) {
      placeholder.value = t('views.session.temperature.placeholder')
    } else if (type.includes('famacha')) {
      placeholder.value = t('views.session.famacha.placeholder')
    } else if (type.includes('dag')) {
      placeholder.value = t('views.session.dag.placeholder')
    }

    showDialog.value = true
  }

  // Handle manual value entry (weight, temperature, FAMACHA, wounds, treatments, etc.)
  async function handleValueSaved({ rawValue, processedValue, date, score, title, description, notes, type: recordType }) {
    const type = sessionType.value
    const goatId = selectedGoat.value.id

    // Format value for display in goat list
    let formatted = rawValue
    if (type.includes('famacha') || type.includes('dag')) {
      formatted = `${score || processedValue}/5`
    } else if (type.includes('temp')) {
      formatted = `${processedValue} °C`
    } else if (type.includes('weight')) {
      formatted = `${processedValue} kg`
    } else if (type.includes('wound') || type.includes('treatment')) {
      // For wounds and treatments, show truncated title
      formatted = title.substring(0, 20) + (title.length > 20 ? '...' : '')
    }

    updateGoatUI(goatId, formatted)
    showDialog.value = false

    // Save to database via stores
    try {
      // Use selected date or current timestamp
      let measuredAt
      if (date) {
        // Date comes as YYYY-MM-DD, use it directly as ISO date
        measuredAt = date
      } else {
        measuredAt = new Date().toISOString().split('T')[0]
      }

      if (type.includes('weight')) {
        await weightStore.addWeight(goatId, processedValue, measuredAt)
      } else if (type.includes('temp')) {
        await temperatureStore.addTemperature(
          goatId,
          processedValue,
          measuredAt
        )
      } else if (type.includes('famacha')) {
        await famachaStore.createFamachaCheck({
          goatId,
          score,
          measuredAt,
        })
      } else if (type.includes('dag')) {
        await dagStore.createDagCheck({
          goatId,
          score,
          measuredAt,
        })
      } else if (type.includes('wound') || type.includes('treatment')) {
        await healthRepository.create({
          goatId,
          type: recordType,
          title,
          description,
          notes,
          measuredAt,
        })
      }

      // TODO: Add DAG store methods when available
      // else if (type.includes('dag')) await dagStore.createDagCheck({ goatId, score: processedValue, measuredAt })

      showToastNotification(t('views.session.valueSaved'))
    } catch (err) {
      console.error(err)
      showToastNotification(t('views.session.savingError'), 'error')
    }
  }

  // Handle weight from photo capture
  async function savePhotoWeight({ weight, distance, metadata, date }) {
    const goat = selectedGoat.value
    if (!goat || !weight) return

    // Validate date is not before goat's birth date
    if (date && goat.dateOfBirth) {
      const selectedDate = new Date(date)
      const birthDate = new Date(goat.dateOfBirth)

      if (
        !isNaN(birthDate.getTime()) &&
        selectedDate.getTime() < birthDate.getTime()
      ) {
        const birthDateString = birthDate.toLocaleDateString()
        showToastNotification(
          `Date cannot be before goat's birth date: ${birthDateString}`,
          'error'
        )
        return
      }
    }

    updateGoatUI(goat.id, `${weight} kg`)

    try {
      // Convert datetime-local format to ISO date
      let measuredAt
      if (date) {
        // Date comes as YYYY-MM-DDTHH:mm from datetime-local input
        // Convert to ISO string for storage
        measuredAt = new Date(date).toISOString()
      } else {
        measuredAt = new Date().toISOString()
      }

      await weightStore.addWeight(goat.id, weight, measuredAt)

      showToastNotification(t('views.session.valueSaved'), 'saved', 3000)
    } catch (err) {
      console.error(err)
      showToastNotification(t('views.session.savingError'), 'error', 5000)
    }
  }

  function handlePhotoWeightError(error) {
    // Error already has message and type ready from PhotoWeightView
    showToastNotification(error.message, error.type, 5000, error.subtitle)
  }

  // Update UI state after value entry
  function updateGoatUI(goatId, formattedValue) {
    enteredValues.value[goatId] = formattedValue
    if (!completedGoats.value.includes(goatId)) {
      completedGoats.value.push(goatId)
    }
  }

  function showToastNotification(
    message,
    type = 'saved',
    duration = 3000,
    subtitle = ''
  ) {
    showToast.value = false
    toastMessage.value = message
    toastType.value = type
    toastDuration.value = duration
    toastSubtitle.value = subtitle
    setTimeout(() => {
      showToast.value = true
    }, 10)
  }

  // Get input mode based on session type
  function getInputMode() {
    const type = sessionType.value
    // wound and treatment use dedicated dialog components, but need valid fallback
    if (type.includes('famacha')) return 'famacha'
    if (type.includes('dag')) return 'dag'
    if (type.includes('temp')) return 'decimal'
    if (type.includes('weight')) return 'decimal'
    if (type.includes('wound')) return 'wound'
    if (type.includes('treatment')) return 'treatment'
    return 'textarea'
  }

  // Get validation rules based on session type
  function getValidation() {
    const type = sessionType.value
    if (type.includes('famacha') || type.includes('dag')) {
      return { type: 'range', min: 1, max: 5 }
    }
    if (type.includes('temp')) {
      return { type: 'range', min: 35, max: 45, unit: '°C' }
    }
    if (type.includes('weight')) {
      return { type: 'range', min: 1, max: 200, unit: 'kg' }
    }
    if (type.includes('wound') || type.includes('treatment')) {
      return { type: 'text', min: 1, max: 200 }
    }
    return {}
  }

  function cancelDialog() {
    showDialog.value = false
  }
</script>
